// Pure helpers for the ImpersonationBanner component.
//
// Kept as a separate module (no JSX) so the unit tests in
// tests/web/impersonation-banner-helpers.test.ts can import them
// without pulling the full React/Vite tree through the Node-pool
// test runtime. The banner target type is inlined structurally here
// (matching the top-nav-helpers.ts pattern) so we don't transitively
// drag api.ts — which uses `import.meta.env` — into the node-pool
// type graph.

export interface ImpersonationBannerUser {
  name: string | null;
  email: string;
  employee_role_slug: string | null;
}

// Role slugs → short labels shown on the banner. Matches
// top-nav-helpers.ts `displayRoleLabel` at the "short" end so the
// banner stays visually compact on narrow screens.
const ROLE_LABELS: Record<string, string> = {
  system_admin: "System Admin",
  company_admin: "Company Admin",
  advisor: "Advisor",
  front_desk: "Front Desk",
  compliance: "Compliance",
  back_office: "Back Office",
};

// Format the banner's target label. Pattern: "NAME · ROLE".
// When the target is a system_admin (admin-on-admin impersonation),
// append " (ADMIN-ON-ADMIN)" so the banner is unmissable even at a
// glance — Grant + Baker have equal rights and this is allowed in
// v1.1, but the sensitivity deserves an explicit visual callout.
// Falls back to email when name is empty, and omits the role segment
// when the slug is unknown (rather than printing "unknown").
export function formatImpersonationTargetLabel(
  user: ImpersonationBannerUser,
): string {
  const displayName = user.name?.trim() || user.email;
  const slug = user.employee_role_slug;
  const roleLabel = slug ? ROLE_LABELS[slug] : null;
  const name = displayName.toUpperCase();
  const base = roleLabel ? `${name} · ${roleLabel.toUpperCase()}` : name;
  if (isAdminOnAdminTarget(user)) {
    return `${base} (ADMIN-ON-ADMIN)`;
  }
  return base;
}

// Return-to-origin memory for impersonation (2026-07-23 audit P6-4). When an
// admin starts impersonating from a page (the Team list), we stash where they
// were + how far they'd scrolled so "Return to my view" lands them back there
// instead of dumping them on /dashboard. This is transient per-device UI
// state (sessionStorage per CLAUDE.md's storage rule), never auth state.
const RETURN_PATH_KEY = "stewardshipos.impersonation.returnPath";
const RETURN_SCROLL_KEY = "stewardshipos.impersonation.returnScroll";

export function saveImpersonationReturn(path: string, scrollY: number): void {
  try {
    sessionStorage.setItem(RETURN_PATH_KEY, path);
    sessionStorage.setItem(RETURN_SCROLL_KEY, String(Math.max(0, scrollY)));
  } catch {
    /* sessionStorage unavailable (private mode / SSR) — non-fatal */
  }
}

// Read + clear the saved return path (null if none).
export function takeImpersonationReturnPath(): string | null {
  try {
    const p = sessionStorage.getItem(RETURN_PATH_KEY);
    sessionStorage.removeItem(RETURN_PATH_KEY);
    return p;
  } catch {
    return null;
  }
}

// Read + clear the saved scroll offset (null if none).
export function takeImpersonationReturnScroll(): number | null {
  try {
    const raw = sessionStorage.getItem(RETURN_SCROLL_KEY);
    sessionStorage.removeItem(RETURN_SCROLL_KEY);
    if (raw === null) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

// True when the target of an impersonation is itself a system_admin.
// Callers use this to switch the banner to its deeper-orange variant
// (via a data-attribute / class) and to render the ADMIN-ON-ADMIN
// suffix on the label.
export function isAdminOnAdminTarget(
  user: ImpersonationBannerUser,
): boolean {
  return user.employee_role_slug === "system_admin";
}
